import { createSelector } from '@reduxjs/toolkit';
import { RootState } from '../index';

// Auth selectors
export const selectAuthState = (state: RootState) => state.auth;
export const selectUser = createSelector(
  selectAuthState,
  (auth) => auth.user
);
export const selectIsAuthenticated = createSelector(
  selectAuthState,
  (auth) => auth.isAuthenticated
);
export const selectIsAuthLoading = createSelector(
  selectAuthState,
  (auth) => auth.isLoading
);
export const selectAuthError = createSelector(
  selectAuthState,
  (auth) => auth.error
);
export const selectIsAuthInitialized = createSelector(
  selectAuthState,
  (auth) => auth.isInitialized
);

// User profile selectors
export const selectUserState = (state: RootState) => state.user;
export const selectUserProfile = createSelector(
  selectUserState,
  (user) => user.currentProfile
);
export const selectUserViewedProfiles = createSelector(
  selectUserState,
  (user) => user.viewedProfiles
);
export const selectUserFriends = createSelector(
  selectUserState,
  (user) => user.friendsMap
);
export const selectUserIsLoading = createSelector(
  selectUserState,
  (user) => user.isLoading
);
export const selectUserError = createSelector(
  selectUserState,
  (user) => user.error
);

// Training selectors
export const selectTrainingState = (state: RootState) => state.training;
export const selectTrainings = createSelector(
  selectTrainingState,
  (training) => training.trainings
);
export const selectTrainingIds = createSelector(
  selectTrainingState,
  (training) => training.trainingIds
);
export const selectTrainingStats = createSelector(
  selectTrainingState,
  (training) => training.stats
);
export const selectTrainingIsLoading = createSelector(
  selectTrainingState,
  (training) => training.isLoading
);
export const selectTrainingError = createSelector(
  selectTrainingState,
  (training) => training.error
);
export const selectPendingChanges = createSelector(
  selectTrainingState,
  (training) => training.pendingChanges
);

// Feed selectors
export const selectFeedState = (state: RootState) => state.feed;
export const selectFeedItemsList = createSelector(
  selectFeedState,
  (feed) => feed.feedItemsList
);
export const selectFeedItemsData = createSelector(
  selectFeedState,
  (feed) => feed.feedItemsData
);
export const selectFeedPosts = createSelector(
  selectFeedState,
  (feed) => feed.posts
);
export const selectFeedIsLoading = createSelector(
  selectFeedState,
  (feed) => feed.isLoading
);
export const selectFeedError = createSelector(
  selectFeedState,
  (feed) => feed.error
);

// Public feed selectors
export const selectPublicFeedState = (state: RootState) => state.publicFeed;
export const selectPublicFeedItemsList = createSelector(
  selectPublicFeedState,
  (publicFeed) => publicFeed.feedItemsList
);
export const selectPublicFeedItemsData = createSelector(
  selectPublicFeedState,
  (publicFeed) => publicFeed.feedItemsData
);
export const selectPublicFeedPosts = createSelector(
  selectPublicFeedState,
  (publicFeed) => publicFeed.posts
);
export const selectPublicFeedIsLoading = createSelector(
  selectPublicFeedState,
  (publicFeed) => publicFeed.isLoading
);
export const selectPublicFeedError = createSelector(
  selectPublicFeedState,
  (publicFeed) => publicFeed.error
);
export const selectPublicFeedBatchCount = createSelector(
  selectPublicFeedState,
  (publicFeed) => publicFeed.fetchedBatchesCount
);

// Belt progression selectors
export const selectBeltProgressionState = (state: RootState) => state.beltProgression;
export const selectBeltPromotions = createSelector(
  selectBeltProgressionState,
  (beltProgression) => beltProgression.promotions
);
export const selectBeltPromotionIds = createSelector(
  selectBeltProgressionState,
  (beltProgression) => beltProgression.promotionIds
);
export const selectCurrentBelts = createSelector(
  selectBeltProgressionState,
  (beltProgression) => beltProgression.currentBelts
);
export const selectBeltProgressionIsLoading = createSelector(
  selectBeltProgressionState,
  (beltProgression) => beltProgression.isLoading
);
export const selectBeltProgressionError = createSelector(
  selectBeltProgressionState,
  (beltProgression) => beltProgression.error
);

// Helper selector to get promotions as an array
export const selectPromotionsArray = createSelector(
  selectBeltPromotions,
  selectBeltPromotionIds,
  (promotions, ids) => ids.map(id => promotions[id]).sort((a, b) => b.date - a.date)
);

// Selector to get belt for a specific martial art
export const selectCurrentBeltForMartialArt = (martialArt: string) =>
  createSelector(
    selectCurrentBelts,
    (currentBelts) => currentBelts[martialArt] || null
  );

// Composite selectors
export const selectUserWithTrainingStats = createSelector(
  selectUserProfile,
  selectTrainingStats,
  (profile, stats) => {
    if (!profile || !stats) return null;

    return {
      ...profile,
      trainingStats: stats
    };
  }
);

export const selectUserWithBelts = createSelector(
  selectUserProfile,
  selectCurrentBelts,
  (profile, belts) => {
    if (!profile) return null;

    return {
      ...profile,
      currentBelts: belts
    };
  }
);

export const selectIsAnyLoading = createSelector(
  selectAuthState,
  selectUserState,
  selectTrainingState,
  selectFeedState,
  selectPublicFeedState,
  selectBeltProgressionState,
  (auth, user, training, feed, publicFeed, beltProgression) => {
    return auth.isLoading ||
           user.isLoading ||
           training.isLoading ||
           feed.isLoading ||
           publicFeed.isLoading ||
           beltProgression.isLoading;
  }
);

export const selectGlobalErrors = createSelector(
  selectAuthState,
  selectUserState,
  selectTrainingState,
  selectFeedState,
  selectPublicFeedState,
  selectBeltProgressionState,
  (auth, user, training, feed, publicFeed, beltProgression) => {
    return {
      auth: auth.error,
      user: user.error,
      training: training.error,
      feed: feed.error,
      publicFeed: publicFeed.error,
      beltProgression: beltProgression.error
    };
  }
);

// Training-specific helpers
export const selectTrainingById = (id: string) => 
  createSelector(
    selectTrainings,
    (trainings) => trainings[id] || null
  );

export const selectTrainingsByType = (type: string) =>
  createSelector(
    selectTrainings,
    selectTrainingIds,
    (trainings, ids) => {
      return ids
        .filter(id => trainings[id]?.type === type)
        .map(id => trainings[id]);
    }
  );

export const selectTrainingsByDateRange = (startDate: number, endDate: number) =>
  createSelector(
    selectTrainings,
    selectTrainingIds,
    (trainings, ids) => {
      return ids
        .filter(id => {
          const training = trainings[id];
          return training && training.date >= startDate && training.date <= endDate;
        })
        .map(id => trainings[id]);
    }
  );

// Feed-specific helpers
export const selectFeedItemById = (id: string) =>
  createSelector(
    [selectFeedItemsList, selectFeedPosts],
    (itemsList, posts) => {
      if (itemsList.includes(id) && posts[id]) {
        return posts[id];
      }
      return null;
    }
  );

export const selectFeedItemsByUser = (userId: string) =>
  createSelector(
    [selectFeedItemsList, selectFeedItemsData, selectFeedPosts],
    (itemsList, itemsData, posts) => {
      return itemsList
        .filter(id => {
          const item = itemsData[id];
          return item && item.owner === userId;
        })
        .map(id => posts[id])
        .filter(Boolean);
    }
  );